import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import DATA_QA from '@automation/data-qa.json';

import { UserService } from '@services/api/user/user.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { StationColumnOptionsService } from '@services/station-column-options.service';
import { TabsComponent } from '@shared/tabs/tabs.component';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { TextButtonComponent } from '@shared/button/text-button/text-button.component';
import {
    SelectComponent,
    SelectionChangeEvent,
} from '@shared/select/select.component';
import { TableViewMenuComponent } from '@features/table-view-menu/table-view-menu.component';
import { RefreshInboxComponent } from '@features/refresh-inbox/refresh-inbox.component';
import { StationDocumentViewComponent } from './document-view/document-view.component';
import { StationDiscrepancyViewComponent } from './discrepancy-view/discrepancy-view.component';
import { TableView } from '@constants/table';
import { ColumnOption, SelectOption } from '@models/shared.model';
import { Role } from '@models/user';
import { isEmailOnlyForAllStations } from '@models/user/user-station';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { isNil } from '@helpers/index';
import { syncOptionsWithHiddenColumns } from '@helpers/table';
import {
    InboxTab,
    InboxTabsData,
    buildDocumentColumnOptions,
    buildNestedDocColumnOptions,
    buildDiscrepancyColumnOptions,
} from '@station/utils';
import { CustomColumn } from '@models/workflow/custom-column';

@Component({
    selector: 'app-station-inbox',
    templateUrl: 'station-inbox.component.html',
    styleUrl: 'station-inbox.component.scss',
    imports: [
        MatDividerModule,
        MatIconModule,
        StationDocumentViewComponent,
        TextButtonComponent,
        TableViewMenuComponent,
        StationDiscrepancyViewComponent,
        RefreshInboxComponent,
        TooltipComponent,
        NgTemplateOutlet,
        TabsComponent,
        SelectComponent,
    ],
})
export class StationInboxComponent implements OnInit {
    @ViewChild(StationDiscrepancyViewComponent)
    discrepancyViewComponent: StationDiscrepancyViewComponent | undefined;
    @ViewChild(StationDocumentViewComponent)
    documentViewComponent: StationDocumentViewComponent | undefined;

    loading = false;
    inboxChoicesLoading: boolean = true;
    selectedTableView: TableView = TableView.Discrepancy;
    docViewColumnOptions: ColumnOption<string>[] = [];
    docViewNestedColumnOptions: ColumnOption<string>[] = [];
    discViewColumnOptions: ColumnOption<string>[] = [];
    selectedInboxTabIndex: number | null = null;
    selectedInboxTab: InboxTab | null = null;
    emailOnlyError: boolean = false;
    userRole: Role | null = null;
    workflowInstanceOptions: SelectOption[] = [];
    selectedWorkflow: number | undefined;

    readonly Role = Role;
    readonly TableView: typeof TableView = TableView;
    readonly tabsData = InboxTabsData;
    readonly dataQa = DATA_QA;

    private documentCustomColumns: CustomColumn[] = [];
    private nestedDocCustomColumns: CustomColumn[] = [];
    private discrepancyCustomColumns: CustomColumn[] = [];
    private lastHiddenColumns: string[] = [];
    private lastNestedHiddenColumns: string[] = [];
    private lastDiscHiddenColumns: string[] = [];
    private _subscriptions = new SubscriptionList() as SubscriptionListType;

    private readonly userService = inject(UserService);
    private readonly workflowService = inject(WorkflowService);
    private readonly columnOptionsService = inject(StationColumnOptionsService);

    get showWorkflowInstanceSelector() {
        const isAdmin =
            this.userRole === Role.Admin ||
            this.userRole === Role.Host_Admin ||
            this.userRole === Role.Station_Manager;

        return isAdmin && this.workflowInstanceOptions.length > 1;
    }

    get workflowSelectionRequired() {
        return (
            this.showWorkflowInstanceSelector && isNil(this.selectedWorkflow)
        );
    }

    get docOptions(): ColumnOption<string>[] {
        return buildDocumentColumnOptions();
    }

    get nestedDocOptions(): ColumnOption<string>[] {
        return buildNestedDocColumnOptions(this.nestedDocCustomColumns);
    }

    get discOptions(): ColumnOption<string>[] {
        return buildDiscrepancyColumnOptions(this.discrepancyCustomColumns);
    }

    get countInformation() {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;

        return table ? table.countInformation : null;
    }

    get lastSyncDate() {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;

        return table ? table.lastSyncDate : undefined;
    }

    ngOnInit() {
        this.setColumnOptions();
        this.checkStationCommunicationType();
        this.getInboxChoices();
    }

    isResetFiltersDisabled(): boolean {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;

        return table ? !table.filterApplied : true;
    }

    resetFilters() {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;
        table?.resetFilters();
    }

    onTableViewChanged(tableView: TableView) {
        this.selectedTableView = tableView;
    }

    refreshInbox() {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;

        table?.refreshData();
    }

    updateColumnDisplay(e: { checked: boolean; id: any }) {
        this.columnOptionsService.updateColumnOptions({
            checked: e.checked,
            id: e.id,
        });
    }

    updateNestedColumnDisplay(e: { checked: boolean; id: any }) {
        this.columnOptionsService.updateNestedColumnOptions({
            checked: e.checked,
            id: e.id,
        });
    }

    updateDiscColumnDisplay(e: { checked: boolean; id: any }) {
        this.columnOptionsService.updateDiscColumnOptions({
            checked: e.checked,
            id: e.id,
        });
    }

    onTabChanged(index: number) {
        const { id } = this.tabsData[index];
        this.selectedInboxTab = id;
    }

    onWorkflowInstanceChange(event: SelectionChangeEvent) {
        this.selectedWorkflow = event.value as number;
        this.refreshTable();
    }

    onDocumentCustomColumnsChange(customColumns: CustomColumn[]) {
        this.documentCustomColumns = customColumns;
        this.nestedDocCustomColumns = customColumns;
        this.rebuildAllColumnViews();
    }

    onDiscrepancyCustomColumnsChange(customColumns: CustomColumn[]) {
        this.discrepancyCustomColumns = customColumns;
        this.rebuildAllColumnViews();
    }

    refreshTable(purge: boolean = true) {
        const table =
            this.discrepancyViewComponent || this.documentViewComponent;
        table?.refreshData(purge);
    }

    private checkStationCommunicationType() {
        this.userService.getUser().subscribe({
            next: (user) => {
                if (!user) return;
                this.userRole = user.role;
                const isFixerOrStationManager =
                    user.role === Role.Fixer ||
                    user.role === Role.Station_Manager;
                this.emailOnlyError =
                    isFixerOrStationManager &&
                    isEmailOnlyForAllStations(user.stations);
            },
        });
    }

    private getInboxChoices() {
        this.inboxChoicesLoading = true;

        this.userService.getStationInboxChoices().subscribe({
            next: (choices) => {
                if (choices) {
                    this.selectedWorkflow = choices.workflowId;
                    this.selectedTableView = choices.discrepancy.isActive
                        ? TableView.Discrepancy
                        : TableView.Document;
                    this.selectedInboxTab = choices.inboxTab;
                    this.selectedInboxTabIndex = this.tabsData.findIndex(
                        (item) => item.id === choices.inboxTab,
                    );
                }
            },
            error: () => {
                this.inboxChoicesLoading = false;
                this.getWorkflows();
            },
            complete: () => {
                this.inboxChoicesLoading = false;
                this.getWorkflows();
            },
        });
    }

    private getWorkflows() {
        this._subscriptions['get-workflows'] = this.workflowService
            .getWorkflows()
            .subscribe({
                next: (items) => {
                    this.workflowInstanceOptions = (items ?? []).map(
                        (item) => ({
                            value: item.id,
                            viewValue: item.name,
                        }),
                    );
                    this.reconcileSelectedWorkflow();
                },
                error: () => {
                    this.workflowInstanceOptions = [];
                    this.selectedWorkflow = undefined;
                },
            });
    }

    private reconcileSelectedWorkflow() {
        const availableWorkflowIds = new Set(
            this.workflowInstanceOptions.map(
                (option) => option.value as number,
            ),
        );
        const selectedWorkflow = this.selectedWorkflow;

        if (
            selectedWorkflow != null &&
            availableWorkflowIds.has(selectedWorkflow)
        ) {
            return;
        }

        if (this.workflowInstanceOptions.length > 0) {
            this.selectedWorkflow = this.workflowInstanceOptions[0]
                .value as number;
            return;
        }

        this.selectedWorkflow = undefined;
    }

    private setColumnOptions() {
        this._subscriptions['column-options'] =
            this.columnOptionsService.columnOptions$.subscribe({
                next: (options) => {
                    this.lastHiddenColumns = options.hiddenColumns;
                    this.docViewColumnOptions = syncOptionsWithHiddenColumns(
                        this.docOptions,
                        options.hiddenColumns,
                    );
                },
            });

        this._subscriptions['nested-column-options'] =
            this.columnOptionsService.nestedColumnOptions$.subscribe({
                next: (options) => {
                    this.lastNestedHiddenColumns = options.hiddenColumns;
                    this.docViewNestedColumnOptions =
                        syncOptionsWithHiddenColumns(
                            this.nestedDocOptions,
                            options.hiddenColumns,
                        );
                },
            });

        this._subscriptions['disc-column-options'] =
            this.columnOptionsService.discColumnOptions$.subscribe({
                next: (options) => {
                    this.lastDiscHiddenColumns = options.hiddenColumns;
                    this.discViewColumnOptions = syncOptionsWithHiddenColumns(
                        this.discOptions,
                        options.hiddenColumns,
                    );
                },
            });
    }

    private rebuildAllColumnViews() {
        this.docViewColumnOptions = syncOptionsWithHiddenColumns(
            this.docOptions,
            this.lastHiddenColumns,
        );
        this.docViewNestedColumnOptions = syncOptionsWithHiddenColumns(
            this.nestedDocOptions,
            this.lastNestedHiddenColumns,
        );
        this.discViewColumnOptions = syncOptionsWithHiddenColumns(
            this.discOptions,
            this.lastDiscHiddenColumns,
        );
    }
}
